import { createAction } from '@reduxjs/toolkit';
import { jwtDecode } from 'jwt-decode';
import { REHYDRATE } from 'redux-persist';
import { CURRENCY_SIGNS, USD_SIGN } from '~/literals';

import { Currency, FlightSearchParams, PassengerLegBaggage, PlainObject, RootState, ServerFlightSearchParams, User } from '~/types';
import { Bag, BagsByLeg, UnitOption, ExtraBagsByLegs, FlightBookingPostParamsValidationMessage, FlightOfferPriceCheckResponse, Leg, ListTypeMessage, PROP_NAME_SPLITER, Passenger, PassengerPricing, PassengerType, UpsellExtras, AdditionalService, BaggageItem, AdditonalServiceType, LegGroup } from '~/types/flights';

export function calculateTotalTripTime(legs: Leg[]): string {
  if (legs.length === 0) return '0 hours 0 minutes';

  const firstDeparture = new Date(legs[0].departure.dateTime);
  const lastArrival = new Date(legs[legs.length - 1].arrival.dateTime);

  const totalMilliseconds = lastArrival.getTime() - firstDeparture.getTime();
  const totalMinutes = Math.floor(totalMilliseconds / (1000 * 60));

  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  const hourText = hours === 1 ? 'hour' : 'hours';
  const minuteText = minutes === 1 ? 'minute' : 'minutes';

  return `${hours}${hourText} ${minutes}${minuteText}`;
}

export function convertToISO8601(timeString: string) {
  const hoursMatch = timeString.match(/(\d+)hrs/);
  const minutesMatch = timeString.match(/(\d+)min/);

  const hours = hoursMatch ? parseInt(hoursMatch[1], 10) : 0;
  const minutes = minutesMatch ? parseInt(minutesMatch[1], 10) : 0;

  return `PT${hours}H${minutes}M`;
}

export function isISO8601Duration(time: string): boolean {
  return parseISO8601Duration(time) !== null;
}

function parseISO8601Duration(duration: string): { days: number; hours: number; minutes: number; seconds: number } | null {
  if (typeof duration !== 'string') return null;

  const matches = duration.match(/^P(?:(\d+)D)?(?:T(?:(\d+)H)?(?:(\d+)M)?(?:(\d+)S)?)?$/);
  if (!matches || !matches.slice(1).some(Boolean)) return null;

  return {
    days: matches[1] ? parseInt(matches[1], 10) : 0,
    hours: matches[2] ? parseInt(matches[2], 10) : 0,
    minutes: matches[3] ? parseInt(matches[3], 10) : 0,
    seconds: matches[4] ? parseInt(matches[4], 10) : 0,
  };
}

export function serverFlightSearchParamsToFlightSearchParam(params: ServerFlightSearchParams): FlightSearchParams {
  return {
    passengers: {
      adults: params.passengers.filter(p => p.passengerType === PassengerType.ADULT).length,
      children: params.passengers.filter(p => p.passengerType === PassengerType.CHILD).length,
      infants: params.passengers.filter(p => p.passengerType === PassengerType.HELD_INFANT || p.passengerType === PassengerType.INFANT).length,
    },
    travelClass: params.cabin,
    type: params.flightType,
    from: {
      title: params.origin.location.friendlyName,
      type: '',
      country: params.origin.country.friendlyName,
      city: params.origin.city.friendlyName,
      iata: params.origin.location.code,
    },
    to: {
      title: params.destination.location.friendlyName,
      type: '',
      country: params.destination.country.friendlyName,
      city: params.destination.city.friendlyName,
      iata: params.destination.location.code,
    },
    departureDate: new Date(params.departureDate),
    returnDate: params.returnDate ? new Date(params.returnDate) : undefined,
    currencyCode: params.currencyCode as Currency,
  }
}

export function getTimeDifferenceInMinutes(start: string, end: string): number {
  const arrivalDate = new Date(start);
  const departureDate = new Date(end);
  const timeDifference = departureDate.getTime() - arrivalDate.getTime();

  return timeDifference / (1000 * 60);
}

export function calculateTimeDifference(startTime: string, endTime: string): string {
  const startDate = new Date(startTime);
  const endDate = new Date(endTime);

  const differenceInMillis = endDate.getTime() - startDate.getTime();

  const totalMinutes = Math.floor(differenceInMillis / (1000 * 60));
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  let formattedDuration = "";

  if (hours > 0) {
    formattedDuration += hours == 1 ? `${hours}hr` : `${hours}hrs`;
  }

  if (minutes > 0) {
    formattedDuration += ` ${minutes}min`;
  }

  return formattedDuration || "0min";
}

export function toHrMinDuration(duration: string): string {
  const parsed = parseISO8601Duration(duration);
  if (!parsed) return duration;

  const totalHours = parsed.days * 24 + parsed.hours;
  const parts: string[] = [];
  if (totalHours > 0) parts.push(`${totalHours}${totalHours === 1 ? 'hr' : 'hrs'}`);
  if (parsed.minutes > 0) parts.push(`${parsed.minutes}min`);
  if (!parts.length && parsed.seconds > 0) parts.push(`${parsed.seconds}sec`);

  return parts.join(' ') || '0min';
}

export const getFriendlyPassengerType = (passengerType: PassengerType): string => {
  if (passengerType.toLocaleLowerCase().includes(PassengerType.ADULT.toLocaleLowerCase())) return "Adult";
  if (passengerType.toLocaleLowerCase().includes(PassengerType.CHILD.toLocaleLowerCase())) return "Child";
  if (passengerType.toLocaleLowerCase().includes(PassengerType.INFANT.toLocaleLowerCase())) return "Infant";
  return '';
}

export function deepCopy<T>(obj: T): T {
  if (obj === null || typeof obj !== 'object') {
    return obj;
  }

  if (Array.isArray(obj)) {
    const arrCopy: any[] = [];
    for (const element of obj) {
      arrCopy.push(deepCopy(element));
    }
    return arrCopy as unknown as T;
  }

  const objCopy: { [key: string]: any } = {};
  for (const key in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, key)) {
      objCopy[key] = deepCopy((obj as { [key: string]: any })[key]);
    }
  }
  return objCopy as T;
}

export function allBookingFieldsValid(params: FlightBookingPostParamsValidationMessage, checkTermsAccepted = false): boolean {
  function checkList(list: ListTypeMessage[]): boolean {
    for (const item of list) {
      if (Object.values(item.fields).some(arr => arr.length > 0)) {
        return false;
      }
      if (item.children && !checkList(item.children)) {
        return false;
      }
    }
    return true;
  }

  return (
    (!params.tripContactPersons || checkList(params.tripContactPersons)) &&
    (!params.passengers || checkList(params.passengers)) &&
    (!params.globalErrorMessages.length) &&
    (!params.termsAccepted.length || !checkTermsAccepted)
  );
}

export function initAssignBagsToPassenger(bags: Bag[], legGroups: LegGroup[], passengerId: string): ExtraBagsByLegs {
  const extraBagsByLegs: ExtraBagsByLegs = {}

  bags.forEach(bag => {
    if (bag.passengerIds.includes(passengerId)) {
      let legIds: string[] = [bag.legIds.join(PROP_NAME_SPLITER)];

      if (bag.bookableByItinerary) {
        legIds = [];
        legGroups.forEach(legGroup => {
          // Get bag leg ids in the leg group
          const groupLegIds = bag.legIds.filter(legId => legGroup.legs.some((leg: Leg) => leg.legId === legId));
          if (groupLegIds.length > 0) {
            legIds.push(groupLegIds.join(PROP_NAME_SPLITER));
          }
        });
      }

      legIds.forEach(legId => {
        const legIdsProp = legId;
        const availableBag = deepCopy(bag);
        availableBag.canBeSold = true;
        availableBag.selected = false;
        availableBag.belongsToMasterLegGroup = false;
        if (!extraBagsByLegs[legIdsProp]) {
          extraBagsByLegs[legIdsProp] = {
            available: [availableBag],
            inCart: [],
          }
        } else {
          extraBagsByLegs[legIdsProp].available.push(availableBag);
        }

      });

    }
  })

  return extraBagsByLegs;
}

export function toWeekDayDateMonYear(dateString: string): string {
  const date = new Date(dateString);
  const dayNames = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
  const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  const dayOfWeek = dayNames[date.getDay()];
  const day = date.getDate();
  const month = monthNames[date.getMonth()];
  const year = date.getFullYear();

  return `${dayOfWeek}, ${day} ${month} ${year}`;
}

export function toHourMinutes(dateString: string): string {
  const date = new Date(dateString);
  const hours = date.getHours().toString().padStart(2, '0');
  const minutes = date.getMinutes().toString().padStart(2, '0');

  return `${hours}:${minutes}`;
}

export function offerSearchDateFormat(date: Date): string {
  const year = date.getFullYear();
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const day = date.getDate().toString().padStart(2, '0');

  return `${year}-${month}-${day}`;
}

export function actionBody<T = any, M extends PlainObject = PlainObject>(
  payload: T,
  meta: M,
): { meta: M; payload: T };
export function actionBody<T = any>(payload: T): { meta: never; payload: T };

export function actionBody<T = any>(payload: T, meta?: any) {
  return { payload, meta };
}

/**
 * Check if cache is valid
 */
export function hasValidCache(lastUpdated: number, max = 10): boolean {
  if (!navigator.onLine) {
    return true;
  }

  return lastUpdated + max * 60 > Date.now();
}

export const rehydrateAction = createAction<RootState>(REHYDRATE);

export const getSearchQueryString = (object: Record<string, any>, nestedLevel: number): string => {
  const keyValuePairs: string[] = [];

  function traverse(object_: Record<string, any>, prefix: string, depth: number) {
    for (const key in object_) {
      if (Object.prototype.hasOwnProperty.call(object_, key)) {
        const value = object_[key];
        const newPrefix = prefix ? `${prefix}.${key}` : key;

        if (typeof value === 'object' && depth < nestedLevel) {
          traverse(value, newPrefix, depth + 1);
        } else {
          keyValuePairs.push(`${encodeURIComponent(newPrefix)}=${encodeURIComponent(value)}`);
        }
      }
    }
  }

  traverse(object, '', 0);

  return keyValuePairs.join('&');
};

export const getUserFromToken = (token: string): User => {
  const tokenData: any = jwtDecode(token);
  const tokenPayload = tokenData.payload;

  return {
    id: tokenPayload.id || '',
    firstName: tokenPayload.firstName || '',
    lastName: tokenPayload.lastName || '',
    email: tokenPayload.email || '',
    mobileNumber: tokenPayload.phone || '',
    addressLine1: tokenPayload.addressLine1 || '',
    addressLine2: tokenPayload.addressLine2 || '',
    addressLine3: tokenPayload.addressLine3 || '',
    postCode: tokenPayload.postCode || '',
    city: tokenPayload.city || '',
    country: tokenPayload.country || '',
    dob: tokenPayload.dob || '',
    passportNumber: tokenPayload.passportNumber || '',
    idNumber: tokenPayload.idNumber || '',
    gender: tokenPayload.gender || '',
    permissions: tokenPayload.permissions || [],
    roles: tokenPayload.roles || [],
  };
};

export const isValidEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  return emailRegex.test(email);
};

export function getTotalBaggageCartPricePerPassenger(passenger: Passenger): string {
  let totalPrice = 0;
  const extraBaggage = passenger.extraBaggage;

  if (extraBaggage) {
    Object.keys(extraBaggage).forEach((key) => {
      const inCartItems = extraBaggage[key].inCart;
      inCartItems.forEach((item) => {
        totalPrice += Number(item.retailPrice?.amount);
      });
    });
  }
  return totalPrice.toFixed(2);
}

export function getTotalBaggageCartPrice(passengers: Passenger[]): string {
  let totalPrice = 0;

  passengers.forEach((passenger) => {
    totalPrice += Number(getTotalBaggageCartPricePerPassenger(passenger));
  });

  return totalPrice.toFixed(2);
}


export const convertToPriceCheckResponse = (json: any): FlightOfferPriceCheckResponse => {
  return {
    priceCheckId: json.priceCheckId,
    dateCreated: json.dateCreated,
    bookingContext: json.bookingContext,
    agentFees: json.agentFees,
    agentFeesOnly: json.agentFeesOnly,
    response: {
      source: json.response.source,
      requestId: json.response.requestId,
      offers: json.response.offers.map((offer: any) => ({
        offerId: offer.offerId,
        flightType: offer.flightType,
        lastTicketDate: offer.lastTicketDate,
        itineraries: {
          outbound: {
            legs: offer.itineraries.outbound.legs.map((leg: any) => ({
              departure: {
                location: {
                  code: leg.departure.location.code,
                  friendlyName: leg.departure.location.friendlyName,
                },
                city: {
                  code: leg.departure.city.code,
                  friendlyName: leg.departure.city.friendlyName,
                },
                country: {
                  code: leg.departure.country.code,
                  friendlyName: leg.departure.country.friendlyName,
                },
                terminal: leg.departure.terminal,
                dateTime: leg.departure.dateTime,
              },
              arrival: {
                location: {
                  code: leg.arrival.location.code,
                  friendlyName: leg.arrival.location.friendlyName,
                },
                city: {
                  code: leg.arrival.city.code,
                  friendlyName: leg.arrival.city.friendlyName,
                },
                country: {
                  code: leg.arrival.country.code,
                  friendlyName: leg.arrival.country.friendlyName,
                },
                terminal: leg.arrival.terminal,
                dateTime: leg.arrival.dateTime,
              },
              carrier: {
                code: leg.carrier.code,
                friendlyName: leg.carrier.friendlyName,
              },
              flightNumber: leg.flightNumber,
              aircraft: {
                code: leg.aircraft.code,
                friendlyName: leg.aircraft.friendlyName,
              },
              operatedBy: {
                code: leg.operatedBy.code,
                friendlyName: leg.operatedBy.friendlyName,
              },
              numberOfStops: leg.numberOfStops,
              duration: leg.duration,
              legId: leg.legId,
              co2Emissions: leg.co2Emissions?.map((emission: any) => ({
                value: emission.value,
                unit: emission.unit,
                cabin: emission.cabin,
              })),
            })),
          },
          inbound: offer.itineraries?.inbound ? {
            legs: offer.itineraries.inbound.legs.map((leg: any) => ({
              departure: {
                location: {
                  code: leg.departure.location.code,
                  friendlyName: leg.departure.location.friendlyName,
                },
                city: {
                  code: leg.departure.city.code,
                  friendlyName: leg.departure.city.friendlyName,
                },
                country: {
                  code: leg.departure.country.code,
                  friendlyName: leg.departure.country.friendlyName,
                },
                terminal: leg.departure.terminal,
                dateTime: leg.departure.dateTime,
              },
              arrival: {
                location: {
                  code: leg.arrival.location.code,
                  friendlyName: leg.arrival.location.friendlyName,
                },
                city: {
                  code: leg.arrival.city.code,
                  friendlyName: leg.arrival.city.friendlyName,
                },
                country: {
                  code: leg.arrival.country.code,
                  friendlyName: leg.arrival.country.friendlyName,
                },
                terminal: leg.arrival.terminal,
                dateTime: leg.arrival.dateTime,
              },
              carrier: {
                code: leg.carrier.code,
                friendlyName: leg.carrier.friendlyName,
              },
              flightNumber: leg.flightNumber,
              aircraft: {
                code: leg.aircraft.code,
                friendlyName: leg.aircraft.friendlyName,
              },
              operatedBy: {
                code: leg.operatedBy.code,
                friendlyName: leg.operatedBy.friendlyName,
              },
              numberOfStops: leg.numberOfStops,
              duration: leg.duration,
              legId: leg.legId,
              co2Emissions: leg.co2Emissions.map((emission: any) => ({
                value: emission.value,
                unit: emission.unit,
                cabin: emission.cabin,
              })),
            })),
          } : undefined,
        },
        price: {
          currency: offer.price.currency,
          billingCurrency: offer.price.billingCurrency,
          total: offer.price.total,
          base: offer.price.base,
          fees: offer.price.fees.map((fee: any) => ({
            amount: fee.amount,
            type: fee.type,
          })),
          grandTotal: offer.price.grandTotal,
          includedCheckedBagsOnly: offer.price.includedCheckedBagsOnly,
          validatingAirlines: offer.price.validatingAirlines.map((airline: any) => ({
            code: airline.code,
            friendlyName: airline.friendlyName,
          })),
          fareType: offer.price.fareType,
        },
        passengerPricing: offer.passengerPricing.map((pricing: any) => ({
          id: pricing.id,
          associatedAdultId: pricing.associatedAdultId,
          passengerType: pricing.passengerType,
          fareOptions: pricing.fareOptions,
          price: {
            currency: pricing.price.currency,
            total: pricing.price.total,
            base: pricing.price.base,
            refundableTaxes: pricing.price.refundableTaxes,
            taxes: pricing.price?.taxes?.map((tax: any) => ({
              amount: tax.amount,
              code: tax.code,
            })),
          },
          fareDetailsByTripLeg: pricing.fareDetailsByTripLeg.map((fareDetail: any) => ({
            legId: fareDetail.legId,
            cabin: fareDetail.cabin,
            fareBases: fareDetail.fareBases,
            brandedFare: fareDetail.brandedFare,
            class: fareDetail.class,
            includedCheckedBags: fareDetail.includedCheckedBags,
          })),
        })),
      })),
      bookingRequirements: {
        emailAddressRequired: json.response.bookingRequirements.emailAddressRequired,
        mobilePhoneNumberRequired: json.response.bookingRequirements.mobilePhoneNumberRequired,
        passengerRequirements: json.response.bookingRequirements.passengerRequirements.map(
          (requirement: any) => ({
            passengerId: requirement.passengerId,
            documentRequired: requirement.documentRequired,
            genderRequired: requirement.genderRequired,
            dateOfBirthRequired: requirement.dateOfBirthRequired,
            redressRequiredIfAny: requirement.redressRequiredIfAny,
            residenceRequired: requirement.residenceRequired,
          })
        ),
      },
      upsellExtras: populateUpsellEXtras(json.response),
      fareRules: json.response.fareRules,
    },
  };
}

function populateUpsellEXtras(response: any): UpsellExtras | undefined {

  if (response?.upsellExtras) {
    let upsellExtras: UpsellExtras = {
      bags: response?.upsellExtras?.bags.map((bag: Bag) => ({
        bagNumber: bag.bagNumber,
        legIds: bag.legIds,
        passengerIds: bag.passengerIds,
        quantity: bag.quantity,
        weight: bag.weight,
        name: bag.name,
        price: bag.price,
        retailPrice: bag.retailPrice,
        bookableByItinerary: bag.bookableByItinerary,
      }))
    };
    return upsellExtras;
  }
  return undefined;
}

export function getStops(legs: number): string {
  const stops = legs - 1;
  if (stops) {
    return stops > 1 ? `${stops} stops` : '1 stop';
  }

  return 'direct';
}

export const shuffleArray = (array: string[]) => {
  for (let i = array.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [array[i], array[j]] = [array[j], array[i]];
  }
};

export function getCurrencySign(currency: Currency): string {
  return CURRENCY_SIGNS[currency] || USD_SIGN;
}

export const getBagsByLeg = (passengerPricing: PassengerPricing[]): BagsByLeg => {

  let processedPassengerTypes = new Set();
  let bagsByLeg: BagsByLeg = {};

  passengerPricing.forEach(passenger => {
    if (!processedPassengerTypes.has(passenger.passengerType)) {
      processedPassengerTypes.add(passenger.passengerType)
      passenger.fareDetailsByTripLeg.forEach(fareDetails => {
        if (!bagsByLeg[fareDetails.legId]) {
          bagsByLeg[fareDetails.legId] = {
            [passenger.passengerType]: {
              value: fareDetails?.includedCheckedBags?.value || '0',
              unit: fareDetails?.includedCheckedBags?.unit || UnitOption.QUANTITY,
            }
          }
        } else {
          bagsByLeg[fareDetails.legId][passenger.passengerType] = {
            value: fareDetails?.includedCheckedBags?.value || '0',
            unit: fareDetails?.includedCheckedBags?.unit || UnitOption.QUANTITY,
          }
        }
      })
    }
  })

  return bagsByLeg;
}

export function getAdditionalServiceBagDescription(additionalService: AdditionalService): string {
  let description = additionalService.value;

  if (additionalService.unit === UnitOption.QUANTITY) {
    description += ' x ' + `${Number(additionalService.value) > 1 ? 'pieces' : 'piece'}`;
  } else if (additionalService.unit === UnitOption.KG || additionalService.unit === UnitOption.LB) {
    description += ' ' + additionalService.unit.toLowerCase();
  }

  return description;
}

export function getIncludedBagsDescription(bags: BaggageItem): string {
  let description = bags.value;

  if (bags.unit === UnitOption.QUANTITY) {
    description += ' x ' + `${Number(bags.value) > 1 ? 'pieces' : 'piece'}`;
  } else if (bags.unit === UnitOption.KG || bags.unit === UnitOption.LB) {
    description += ' ' + bags.unit.toLowerCase();
  }

  return description;
}

export function getPassengerLegBaggage(passengersPricing: PassengerPricing[], passengers: Passenger[]): PassengerLegBaggage[] {
  let passengerLegBaggage: PassengerLegBaggage[] = [];
  passengersPricing.forEach(pp => {
    const fareDetailsByTripLeg = pp.fareDetailsByTripLeg;
    fareDetailsByTripLeg.forEach(fareDetail => {
      const passenger = passengers.find(p => p.id === pp.id);

      const included = fareDetail.includedCheckedBags ? getIncludedBagsDescription(fareDetail.includedCheckedBags) : undefined;

      const chargeableBagsAdditionalService = fareDetail.additionalServices?.find((service: AdditionalService) => service.type === AdditonalServiceType.CHARGEABLE_CHECKED_BAGS && service.value !== '0');
      const chargeable = chargeableBagsAdditionalService ? getAdditionalServiceBagDescription(chargeableBagsAdditionalService) : undefined;

      if (passenger) {
        const baggage: PassengerLegBaggage = {
          fistName: passenger.firstName,
          lastName: passenger.lastName,
          passengerId: passenger.id,
          legId: fareDetail.legId,
          included,
          chargeable,
        };
        passengerLegBaggage.push(baggage);
      }
    });
  })

  return passengerLegBaggage;
}

export function temporary_BagNumberControlToSelectSameBagNumberInAllItineraries(extraBagsByLeg: ExtraBagsByLegs, legIds: string, bagNumber?: string, clear = false) {
  // Temporary function to set other bags to not selectable if first choice is selected
  // Amadeus is refusing to sell different bag numbers for different itineraries
  // This function should be removed when Amadeus responds to enquiry and give us a solution
  if (!extraBagsByLeg) return;
  if (clear && !bagNumber) {
    // If clear, reset all bags to selectable with no master leg group
    Object.entries(extraBagsByLeg).forEach(([_, extraBags]) => {
      extraBags.available.forEach((bag: Bag) => {
        bag.selected = false;
        bag.canBeSold = true;
        bag.belongsToMasterLegGroup = false;
      });
      extraBags.inCart = [];
    });

  } else {
    let masterLegGroupIds: string;

    // Hunt for current master leg group
    Object.entries(extraBagsByLeg).forEach(([currentLegIds, extraBags]) => {
      if (extraBags.available.find((bag: Bag) => bag.belongsToMasterLegGroup)) {
        masterLegGroupIds = currentLegIds;
      }
    })

    Object.entries(extraBagsByLeg).forEach(([currentLegIds, extraBags]) => {
      extraBags.available.forEach((bag: Bag) => {
        // Only sell bags with same bagNumber
        if (bag.bagNumber == bagNumber || masterLegGroupIds == currentLegIds) {
          bag.canBeSold = true;
        } else {
          bag.canBeSold = false;
          bag.selected = false;
        }

        // If no master group, current group claims master group
        if (!masterLegGroupIds && currentLegIds === legIds) {
          bag.belongsToMasterLegGroup = true;
          bag.canBeSold = true;
        }
      });

      // clear carts with bags not the same as in master group
      extraBags.inCart = extraBags.inCart.filter((bag: Bag) => {
        return bag.bagNumber == bagNumber || masterLegGroupIds == currentLegIds;
      });
    });
  }
}